import $window from './window';
import { rootLogger } from '../components/useLog/useLog';

/**
 * Settings and constants for Signifyd device fingerprinting
 */
const SETTINGS = {
  key: 'anf-signifyd-fingerprint',
  scriptId: 'sig-api',
  expirationTime: 60 * 60 * 24 * 1000, // 24 hours
  expirationPage: 'order-confirmation',
};

/**
 * Generate random bytes for session ID creation
 * @returns {string} 32 character hex string
 */
function getRandomBytes() {
  if (!$window?.crypto?.getRandomValues) {
    // Fallback for environments without crypto.getRandomValues
    return (
      Math.random().toString(36).substring(2, 15)
      + Math.random().toString(36).substring(2, 15)
    );
  }

  const buffer = new Uint8Array(16);
  $window.crypto.getRandomValues(buffer);
  return Array.from(buffer)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Store session ID in sessionStorage with expiration
 * @param {string} value - Session ID to store
 * @returns {Object|null} Stored item or null on error
 */
function setSessionStorageItem(value) {
  const item = {
    value,
    expiry: Date.now() + SETTINGS.expirationTime,
  };

  try {
    $window?.sessionStorage?.setItem(SETTINGS.key, JSON.stringify(item));
    return item;
  } catch (error) {
    rootLogger
      .logger('signifydSession')
      .error('Failed to store Signifyd session ID:', error)
      .destroy();
    return null;
  }
}

/**
 * Get session ID from sessionStorage
 * @returns {Object|null} Stored item or null
 */
function getSessionStorageItem() {
  try {
    const raw = $window?.sessionStorage?.getItem(SETTINGS.key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    rootLogger
      .logger('signifydSession')
      .error('Failed to retrieve Signifyd session ID:', error)
      .destroy();
    return null;
  }
}

/**
 * Check if stored session ID is expired
 * @returns {boolean} True if expired
 */
function isSessionExpired() {
  const item = getSessionStorageItem();
  return !!item && Date.now() >= item.expiry;
}

/**
 * Remove session ID from sessionStorage
 */
function removeSessionId() {
  try {
    $window?.sessionStorage?.removeItem(SETTINGS.key);
  } catch (error) {
    rootLogger
      .logger('signifydSession')
      .error('Failed to remove Signifyd session ID:', error)
      .destroy();
  }
}

/**
 * Create new session ID and store it
 * @returns {string} New session ID
 */
function createSessionId() {
  const sessionId = getRandomBytes();
  setSessionStorageItem(sessionId);
  return sessionId;
}

/**
 * Extract session ID from URL query parameter
 * @returns {string|null} Session ID from URL or null
 */
function getSessionIdFromUrl() {
  const href = $window?.location?.href || '';
  const match = href.match(/sigSessID=([\w-]+)&?/);
  return match ? match[1] : null;
}

/**
 * Check if we're on the order confirmation page
 * @returns {boolean} True if on expiration page
 */
function isOnExpirationPage() {
  const dataPage = $window?.document?.body?.getAttribute?.('data-page') || '';
  return dataPage.includes(SETTINGS.expirationPage);
}

/**
 * Get or create Signifyd session ID
 * @returns {string|null} Session ID or null if Signifyd is disabled
 */
export function getSignifydSessionId() {
  // Check if Signifyd is enabled (you may need to adjust this check based on your config)
  const isSignifydEnabled = $window?.ANF?.params?.storeAttributes?.signifydFeatureEnabled
                           || $window?.signifydEnabled
                           || false;

  if (!isSignifydEnabled) {
    return null;
  }

  // If on expiration page, clean up and return null
  if (isOnExpirationPage()) {
    removeSessionId();
    return null;
  }

  // Check for session ID in URL first
  const urlSessionId = getSessionIdFromUrl();
  if (urlSessionId) {
    setSessionStorageItem(urlSessionId);
    return urlSessionId;
  }

  // Check for existing session ID
  const storedItem = getSessionStorageItem();
  if (storedItem?.value) {
    // Check if expired
    if (isSessionExpired()) {
      removeSessionId();
      return createSessionId();
    }

    // Refresh expiration time
    setSessionStorageItem(storedItem.value);
    return storedItem.value;
  }

  // Create new session ID
  return createSessionId();
}

/**
 * Initialize Signifyd device fingerprinting script
 * @param {string} scriptSrc - URL to Signifyd script
 * @returns {boolean} True if script was created/exists
 */
export function initializeSignifydScript(scriptSrc) {
  if (!scriptSrc || !$window?.document) {
    return false;
  }

  const sessionId = getSignifydSessionId();
  if (!sessionId) {
    return false;
  }

  // Check if script already exists
  const existingScript = $window.document.getElementById(SETTINGS.scriptId);
  if (existingScript) {
    return true;
  }

  try {
    // Create and append script tag
    const script = $window.document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptSrc;
    script.id = SETTINGS.scriptId;
    script.setAttribute('data-order-session-id', sessionId);

    $window.document.body.appendChild(script);
    return true;
  } catch (error) {
    rootLogger
      .logger('signifydSession')
      .error('Failed to create Signifyd script:', error)
      .destroy();
    return false;
  }
}

/**
 * Clean up Signifyd session and script
 */
export function cleanupSignifydSession() {
  removeSessionId();

  try {
    const script = $window?.document?.getElementById?.(SETTINGS.scriptId);
    if (script) {
      script.remove();
    }
  } catch (error) {
    rootLogger
      .logger('signifydSession')
      .error('Failed to cleanup Signifyd script:', error)
      .destroy();
  }
}

/**
 * Set custom expiration time for session
 * @param {number} milliseconds - Expiration time in milliseconds
 */
export function setSignifydSessionExpiration(milliseconds) {
  if (typeof milliseconds === 'number' && milliseconds > 0) {
    SETTINGS.expirationTime = milliseconds;
  }
}

export default {
  getSignifydSessionId,
  initializeSignifydScript,
  cleanupSignifydSession,
  setSignifydSessionExpiration,
};
